import React, { useState } from "react";
import { usePageMeta } from "@/lib/usePageMeta";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";

export default function Signup() {
  const { t } = useI18n();
  usePageMeta({ title: t("signup_title"), noindex: true });
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) {
      toast.error(t("password_mismatch"));
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { name: name.trim() || email.split("@")[0] } },
      });
      if (error) {
        toast.error(error.message || t("auth_error"));
        return;
      }
      // If email confirmation is off in Supabase settings, signUp returns an
      // active session immediately; otherwise there's no session yet and the
      // user needs to click the confirmation link first.
      if (data?.session) {
        nav("/", { replace: true });
      } else {
        setSent(true);
      }
    } catch {
      toast.error(t("auth_error"));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-sm mx-auto py-24 text-center animate-in fade-in duration-500">
        <h1 className="text-2xl font-serif-en mb-3">{t("signup_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("signup_check_email")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto py-16 px-4 sm:px-0 animate-in fade-in duration-500">
      <h1 className="text-2xl font-serif-en mb-6 text-center">{t("signup_title")}</h1>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="signup-name" className="text-sm text-muted-foreground">{t("signup_name")}</label>
          <Input id="signup-name" value={name} onChange={e => setName(e.target.value)} autoComplete="name" data-testid="signup-name-input" />
        </div>
        <div>
          <label htmlFor="signup-email" className="text-sm text-muted-foreground">{t("login_email")}</label>
          <Input id="signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
            autoComplete="email" data-testid="signup-email-input" />
        </div>
        <div>
          <label htmlFor="signup-password" className="text-sm text-muted-foreground">{t("login_password")}</label>
          <Input id="signup-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
            autoComplete="new-password" data-testid="signup-password-input" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
          {loading ? "…" : t("signup_create")}
        </Button>
      </form>
      <p className="text-sm text-center text-muted-foreground mt-6">
        {t("signup_have_account")}{" "}
        <Link to="/login" className="text-primary underline underline-offset-4" data-testid="signup-login-link">{t("signup_login_link")}</Link>
      </p>
    </div>
  );
}
